import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { upsertProfile } from '../lib/queries';
import { useCartStore } from '../store/cartStore';
import { useProfile } from './useUserData';

const REDIRECT_URI = `${window.location.origin}/callback`;

// Module-level (NOT a per-instance ref): the post-login profile sync must run
// ONCE per session, not once per component that happens to call useAuth().
let syncedUserId: string | null = null;

/**
 * Single auth hook for the whole app, backed by Supabase Auth.
 * After sign-in:
 *  1. Supabase Auth issues a session — supabase-js persists it and attaches
 *     it to every request automatically, so RLS policies see the right user.
 *  2. Upserts the user profile into the `profiles` table.
 */
export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const user = session?.user;
  const userId = user?.id;

  // Shared react-query cache keyed by ['profile', userId] — every component
  // that calls useAuth() reads the same cached entry.
  const { data: profile } = useProfile(userId);
  const profileDisplayName = profile?.display_name as string | undefined;
  const profileAvatarUrl = (profile?.avatar_url as string | undefined) ?? null;

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setIsLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setIsLoading(false);
      if (!newSession) syncedUserId = null;
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    (async () => {
      if (!user || syncedUserId === user.id) return;
      syncedUserId = user.id;

      try {
        // ── Ensure profile exists (insert-only — never clobber a
        // username/display_name the user has since customized in Settings
        // or the Storefront editor on subsequent logins) ──
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const db2 = supabase as any;
        const existing = await db2.from('profiles').select('id').eq('id', user.id).maybeSingle();
        if (!existing?.data) {
          const meta = user.user_metadata ?? {};
          const username =
            (meta.username as string | undefined) ??
            (meta.user_name as string | undefined) ?? // GitHub's OAuth metadata field
            user.email?.split('@')[0] ??
            `user_${user.id.slice(-6)}`;

          const { error: upsertErr } = await upsertProfile({
            id:           user.id,
            username,
            display_name: (meta.full_name as string | undefined) ?? (meta.name as string | undefined) ?? username,
            email:        user.email ?? null,
            avatar_url:   (meta.avatar_url as string | undefined) ?? (meta.picture as string | undefined) ?? null,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any);
          if (upsertErr) console.error('[profile upsert failed]', upsertErr);
          else queryClient.invalidateQueries({ queryKey: ['profile', user.id] });
        }

        // ── Apply pending tier chosen during signup ──
        const pendingTier = localStorage.getItem('aiphra_pending_tier') as 'free' | 'starter' | 'pro' | 'elite' | null;
        if (pendingTier && ['free', 'starter', 'pro', 'elite'].includes(pendingTier)) {
          localStorage.removeItem('aiphra_pending_tier');
          await db2.from('profiles').update({ subscription_tier: pendingTier }).eq('id', user.id);
        }

        queryClient.invalidateQueries({ queryKey: ['profile', user.id] });
      } catch (e) {
        console.warn('[auth] post-login profile sync failed:', e);
      }
    })();
  }, [user, queryClient]);

  const displayName = profileDisplayName
    ?? (user?.user_metadata?.full_name as string | undefined)
    ?? (user?.user_metadata?.name as string | undefined)
    ?? user?.email
    ?? 'My Account';

  return {
    isLoggedIn: !!session,
    isLoading,
    session,
    signInWithPassword: (email: string, password: string) =>
      supabase.auth.signInWithPassword({ email, password }),
    signUpWithPassword: (email: string, password: string) =>
      supabase.auth.signUp({ email, password, options: { emailRedirectTo: REDIRECT_URI } }),
    signInWithGoogle: () =>
      supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: REDIRECT_URI } }),
    signInWithGithub: () =>
      supabase.auth.signInWithOAuth({ provider: 'github', options: { redirectTo: REDIRECT_URI } }),
    logout: async () => {
      useCartStore.getState().clearCart();
      await supabase.auth.signOut();
    },
    user,
    userId,
    displayName,
    avatarUrl:      profileAvatarUrl,
    avatarInitials: displayName.charAt(0).toUpperCase(),
  };
}
